/**
 * The event header.
 *
 * Rules already allow organisers to create and update these; nothing in
 * `firestore.rules` changes for Stage A's event configuration screen.
 *
 * Timestamps convert at this boundary and nowhere else: Firestore holds
 * `Timestamp`, the domain and the UI hold `Date`, and neither knows about the
 * other. That is the convention, and it is what keeps `packages/domain` free
 * of Firestore types.
 */

import { doc, getDoc, onSnapshot, Timestamp, updateDoc } from 'firebase/firestore';

import type { CairnEvent, EventId } from '@cairn/domain';

import { getServices } from './firebase.js';

/** The fields an organiser may edit. `id` is not among them — it is the path. */
export type EventEdits = Omit<CairnEvent, 'id'>;

/**
 * A Firestore document is `unknown` until proven otherwise. Reading a field
 * that was never written must not throw here — an event created before a field
 * existed is a normal thing to meet, and the screen should render it rather
 * than white-screen.
 */
function toEvent(id: string, data: Record<string, unknown>): CairnEvent {
  return {
    id: id as EventId,
    name: (data['name'] as string | undefined) ?? '',
    timezone: (data['timezone'] as string | undefined) ?? 'Europe/London',
    startsAt: toDate(data['startsAt']),
    endsAt: toDate(data['endsAt']),
    defaultMaxDwellMinutes: (data['defaultMaxDwellMinutes'] as number | undefined) ?? 60,
    medicalProvider: (data['medicalProvider'] as string | null | undefined) ?? null,
  };
}

/**
 * An absent or malformed timestamp becomes an Invalid Date rather than `now`.
 * `validateEvent` then reports it, which is honest — quietly substituting the
 * current time would hide a broken document behind a plausible-looking form.
 */
function toDate(value: unknown): Date {
  return value instanceof Timestamp ? value.toDate() : new Date(Number.NaN);
}

export async function getEvent(eventId: string): Promise<CairnEvent | null> {
  const { db } = await getServices();
  const snapshot = await getDoc(doc(db, 'events', eventId));
  if (!snapshot.exists()) return null;
  return toEvent(snapshot.id, snapshot.data());
}

/**
 * Live, because two organisers configuring the same event at once is ordinary
 * and neither should be editing a stale copy.
 */
export async function watchEvent(
  eventId: string,
  onChange: (event: CairnEvent | null) => void,
): Promise<() => void> {
  const { db } = await getServices();

  return onSnapshot(doc(db, 'events', eventId), (snapshot) => {
    onChange(snapshot.exists() ? toEvent(snapshot.id, snapshot.data()) : null);
  });
}

/**
 * Writes the editable fields. Deliberately not a create: the first event on a
 * project comes from `tools/bootstrap` under D56, because the first organiser
 * has to exist before anyone can write anything here.
 */
export async function updateEvent(eventId: string, edits: EventEdits): Promise<void> {
  const { db } = await getServices();

  await updateDoc(doc(db, 'events', eventId), {
    name: edits.name,
    timezone: edits.timezone,
    startsAt: Timestamp.fromDate(edits.startsAt),
    endsAt: Timestamp.fromDate(edits.endsAt),
    defaultMaxDwellMinutes: edits.defaultMaxDwellMinutes,
    medicalProvider: edits.medicalProvider,
  });
}
